import { useState, useEffect, useRef } from 'react';
import { getTodayDate, getDateDaysAgo } from '../utils/date';
import { getDailyNutrition, getTargetRanges } from '../services/nutrition.service';
import { getWorkoutEntriesForDate } from '../services/workout.service';
import { calculateDailyTDEE } from '../services/tdee.service';
import { qualifiesForWorkoutConsistency } from '../utils/workoutConsistency';
import type { WorkoutForConsistency } from '../utils/workoutConsistency';

/** localStorage key for cached consistency snapshot */
const CACHE_KEY = 'coretrak.consistency.cache';
/** Bump this when cached schema changes — forces all clients to recompute */
const CACHE_VERSION = 5;

export interface ConsistencyDay {
  hasMeals: boolean;
  proteinOnTarget: boolean;
  hasStrength: boolean;
  deficitOnTarget: boolean;
}

export interface ConsistencyData {
  days: ConsistencyDay[];
  dates: string[];
  proteinCount: number;
  loggingCount: number;
  strengthCount: number;
  strengthTarget: number;
  deficitCount: number;
  loading: boolean;
}

/** Default empty state — loading=true triggers skeleton. */
const DEFAULT_DATA: ConsistencyData = {
  days: [],
  dates: [],
  proteinCount: 0,
  loggingCount: 0,
  strengthCount: 0,
  strengthTarget: 2,
  deficitCount: 0,
  loading: true,
};

// ============================================================
// CACHE
// ============================================================
function readCache(uid: string): ConsistencyData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed._v !== CACHE_VERSION) return null;
    if (parsed._date === getTodayDate() && parsed._uid === uid) {
      return {
        days: parsed.days,
        dates: parsed.dates,
        proteinCount: parsed.proteinCount,
        loggingCount: parsed.loggingCount,
        strengthCount: parsed.strengthCount,
        strengthTarget: parsed.strengthTarget,
        deficitCount: parsed.deficitCount ?? 0,
        loading: false,
      };
    }
  } catch { /* ignore corrupt cache */ }
  return null;
}

function writeCache(uid: string, data: ConsistencyData): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      ...data,
      _v: CACHE_VERSION,
      _date: getTodayDate(),
      _uid: uid,
    }));
  } catch { /* storage full — not critical */ }
}

// ============================================================
// MODULE-LEVEL SINGLE-FLIGHT
// Checked and set synchronously before any await — no race possible.
// ============================================================
const _inFlight = new Map<string, Promise<ConsistencyData>>();

/**
 * Fetch 7-day consistency data for a user.
 * Module-level: all hook instances share this function and its gate.
 * - Cache hit: returns immediately, no Firestore reads.
 * - In-flight: awaits existing promise, no duplicate reads.
 * - Cold fetch: registers promise synchronously, then fetches.
 */
async function fetchConsistencyData(
  userId: string,
  isDemoMode: boolean,
  force: boolean,
  days: number = 7,
): Promise<ConsistencyData> {
  const uid = isDemoMode ? 'demo' : userId;
  const key = `${uid}|${getTodayDate()}|${days}`;

  // Cache hit — return immediately
  if (!force) {
    const cached = readCache(uid);
    if (cached) return cached;
  }

  // In-flight — await existing promise (synchronous check before any await)
  if (!force && _inFlight.has(key)) {
    return _inFlight.get(key)!;
  }

  // Cold fetch — register promise synchronously before first await
  const promise = (async (): Promise<ConsistencyData> => {
    try {
      const today = getTodayDate();
      const dates: string[] = [];
      for (let i = days - 1; i >= 0; i--) {
        dates.push(i === 0 ? today : getDateDaysAgo(i));
      }

      const strengthTarget = 2;

      if (isDemoMode) {
        const { demoData } = await import('../demo');
        const allWorkouts = demoData.allWorkouts();
        const allDaily = demoData.allDailyNutrition();
        const targets = demoData.targets;
        const proteinMin = targets.protein?.min ?? 160;
        const proteinThreshold = proteinMin * 0.8;

        const demoPattern: ConsistencyDay[] = dates.map(dateStr => {
          const dayNutrition = allDaily.find(d => d.date === dateStr);
          const hasMeals = (dayNutrition?.totalCalories ?? 0) > 0;
          const proteinOnTarget = hasMeals && (dayNutrition?.totalProtein ?? 0) >= proteinThreshold;
          const hasStrength = allWorkouts.some(
            w => w.date === dateStr && qualifiesForWorkoutConsistency(w)
          );
          const totalCalories = dayNutrition?.totalCalories ?? 0;
          const deficitOnTarget = hasMeals && totalCalories > 0 && totalCalories < 1800;
          return { hasMeals, proteinOnTarget, hasStrength, deficitOnTarget };
        });

        const result: ConsistencyData = {
          days: demoPattern,
          dates,
          proteinCount: demoPattern.filter(d => d.proteinOnTarget).length,
          loggingCount: demoPattern.filter(d => d.hasMeals).length,
          strengthCount: demoPattern.filter(d => d.hasStrength).length,
          strengthTarget,
          deficitCount: demoPattern.filter(d => d.deficitOnTarget).length,
          loading: false,
        };
        writeCache(uid, result);
        return result;
      }

      // Production: get protein target once
      let proteinTarget = 140;
      try {
        const targets = await getTargetRanges(userId);
        if (targets?.protein?.min && targets.protein.min > 0) {
          proteinTarget = targets.protein.min;
        }
      } catch { /* use default */ }

      const proteinThreshold = proteinTarget * 0.8;

      const dayResults = await Promise.all(
        dates.map(async (date): Promise<ConsistencyDay | null> => {
          try {
            const isToday = date === today;

            const [dailyTdee, dailyNutrition, workouts] = await Promise.all([
              calculateDailyTDEE(userId, date),
              getDailyNutrition(userId, date),
              getWorkoutEntriesForDate(userId, date),
            ]);

            const hasMeals = (dailyNutrition?.totalCalories || 0) > 0;
            const totalProtein = dailyNutrition?.totalProtein || 0;
            const proteinOnTarget = hasMeals && totalProtein >= proteinThreshold;
            const hasStrength = workouts.some(
              (w: WorkoutForConsistency) => qualifiesForWorkoutConsistency(w)
            );
            const deficitOnTarget = !isToday && hasMeals && dailyTdee.deficit >= 200;

            return { hasMeals, proteinOnTarget, hasStrength, deficitOnTarget };
          } catch (error) {
            console.error(`[useConsistencyStrip] Failed for ${date}:`, error);
            return null;
          }
        })
      );

      const filteredDays = dayResults.filter((d): d is ConsistencyDay => d !== null);

      const result: ConsistencyData = {
        days: filteredDays,
        dates: dates.slice(0, filteredDays.length),
        proteinCount: filteredDays.filter(d => d.proteinOnTarget).length,
        loggingCount: filteredDays.filter(d => d.hasMeals).length,
        strengthCount: filteredDays.filter(d => d.hasStrength).length,
        strengthTarget,
        deficitCount: filteredDays.filter(d => d.deficitOnTarget).length,
        loading: false,
      };
      writeCache(uid, result);
      return result;
    } finally {
      // Always clear on completion or error — retries remain possible
      _inFlight.delete(key);
    }
  })();

  _inFlight.set(key, promise);
  return promise;
}

// ============================================================
// HOOK — thin wrapper around fetchConsistencyData
// ============================================================

/**
 * 7-Day Consistency Strip hook
 *
 * Computes rolling 7-day behavioural adherence:
 * - Logging: did the user log any meals that day? (totalCalories > 0)
 * - Protein: did total protein reach >= 80% of daily minimum target?
 * - Strength: did the user log at least one qualifying workout?
 *   (gym, run, other, or walk >= 30 min)
 */
export function useConsistencyStrip(
  userId: string | undefined,
  isDemoMode: boolean,
  refreshKey?: number,
  days: number = 7,
): ConsistencyData {
  const [data, setData] = useState<ConsistencyData>(() => {
    const uid = isDemoMode ? 'demo' : userId;
    if (!uid) return DEFAULT_DATA;
    return readCache(uid) || DEFAULT_DATA;
  });

  const prevRefreshKeyRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!userId && !isDemoMode) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    const isExplicitRefresh =
      refreshKey !== undefined &&
      refreshKey !== prevRefreshKeyRef.current &&
      prevRefreshKeyRef.current !== undefined;
    prevRefreshKeyRef.current = refreshKey;

    fetchConsistencyData(userId ?? 'demo', isDemoMode, isExplicitRefresh, days)
      .then(result => setData(result))
      .catch(() => setData(prev => ({ ...prev, loading: false })));
  }, [userId, isDemoMode, refreshKey, days]);

  return data;
}
